import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { findTriggeringComment } from '../utils/notificationInbox'
import type { NotificationDocument } from '@/lib/db/rxdb/schemas/notification'
import { CommentItem } from '@/components/features/comments/CommentItem'
import { useComments } from '@/lib/db/rxdb/hooks'

interface NotificationConversationProps {
  /** Comment entity type as the client stores it: `work_item` or `page`. */
  entityType: string
  entityId: string
  envId: string
  notification: NotificationDocument
}

/**
 * The task's (or doc's) comments, oldest first, reusing the task-detail
 * CommentItem (reactions, replies, edit). The comment that triggered the
 * notification gets a brand-tint background and a left border, and is
 * scrolled into view.
 */
export function NotificationConversation({
  entityType,
  entityId,
  envId,
  notification,
}: NotificationConversationProps) {
  const { t } = useTranslation('notifications')
  const containerRef = useRef<HTMLDivElement>(null)
  const { comments, isLoading } = useComments({ entityType, entityId, isDeleted: false })

  const scoped = useMemo(
    () => comments.filter((c) => !envId || c.envId === envId),
    [comments, envId],
  )
  const topLevel = useMemo(
    () => scoped.filter((c) => !c.parentCommentId).sort((a, b) => a.createdOn - b.createdOn),
    [scoped],
  )
  const highlightedThreadId = useMemo(() => {
    const trigger = findTriggeringComment(scoped, notification)
    if (!trigger) return null
    return trigger.parentCommentId ?? trigger.id
  }, [scoped, notification])

  useEffect(() => {
    if (!highlightedThreadId) return
    const el = containerRef.current?.querySelector('[data-highlighted="true"]')
    if (el && 'scrollIntoView' in el) el.scrollIntoView({ block: 'nearest' })
  }, [highlightedThreadId])

  if (isLoading) {
    return <p className="py-4 text-[13px] text-muted-foreground">{t('pane.loadingConversation')}</p>
  }
  if (topLevel.length === 0) {
    return <p className="py-4 text-[13px] text-muted-foreground">{t('pane.noComments')}</p>
  }

  return (
    <div ref={containerRef} className="space-y-1" data-testid="notification-conversation">
      {topLevel.map((comment) => {
        const isHighlighted = comment.id === highlightedThreadId
        return (
          <CommentItem
            key={comment.id}
            comment={comment}
            replies={scoped
              .filter((c) => c.parentCommentId === comment.id)
              .sort((a, b) => a.createdOn - b.createdOn)}
            entityType={entityType}
            entityId={entityId}
            workspaceId={envId}
            level={0}
            highlighted={isHighlighted}
            cardClassName={
              isHighlighted
                ? 'rounded-l-none border-y-transparent border-r-transparent border-l-2 border-l-brand bg-brand-tint/60 hover:border-l-brand'
                : 'border-transparent bg-transparent hover:border-transparent'
            }
          />
        )
      })}
    </div>
  )
}
